import { BracketType, BracketTypeEnum } from "../types";
import { Competitor, Status } from "../Competitors/types";
import { WrestlingResult } from "../../Scoreboard/Wrestling/wrestlingStore";
import { JudoResult } from "../../Scoreboard/Judo/judoStore";
import { FightingResult } from "../../Scoreboard/Fighting/fightingStore";
import { ScoreboardType } from "../../Scoreboard/Common/types";

export type Fight = {
    id: number;
    fightNumber: number;
    bracketType: BracketType;
};

export type FightTime = {
    time: string;
    time_in_seconds: number;
};

export const StatusEnum = {
    None: 0,
    AwaitingCompetitors: 1,
    FightInProgress: 2,
};

export type StatusEnumType = (typeof StatusEnum)[keyof typeof StatusEnum];

export type FightQueueStatus = {
    fightId: number;
    status: StatusEnumType;
};

export type ComplexFight = {
    id: number;
    eventId: number;
    matId: number;
    parameterId: number;
    bracketLevel: number;
    category: string;
    roundName: string;
    bracketType: BracketType;
    firstCompetitor: Competitor;
    secondCompetitor: Competitor;
    winnerCompetitor: Competitor | null;
    fightTime: FightTime;
    isReadyToPlay: boolean;
    fightNumber: number | null;
    //FIXME: These two below come from finished fight type. Adapt type in TS too.
    matName?: string;
    finished?: string;
};

//TODO Now it covers both finished and upcoming fights. Fix it. By using only ComplexFight for both.
export type OrganizerComplexFight = ComplexFight & {
    duplicatedPair: boolean;
};

export const SUPPORTED_RESULT_TYPES = [
    "bye",
    "bjj2",
    "bjj3",
    "newaza",
    "submissionFighting",
    "doubleDisqualification",
    "mmaSimplified",
    "submissionOnly",
    "submissionOnlyWithPenalties",
    "combatJuJutsu",
    "wrestling",
    "grappling",
    "judo",
    "taekwondo",
    "karateKumite",
    "fighting",
    "fightCancelled",
] as const;

export type ResultType = (typeof SUPPORTED_RESULT_TYPES)[number];

//TODO: Add more fields to Bjj2Result
export type Bjj2Result = {
    advantages1: number;
    advantages2: number;
};

export type TaekwondoResult = {
    winnerCompetitorId: number;
    type: ResultType;
    time: string;
    fightFinishType: string;
    submission: string | null;
    roundNumber: string;
    points1: number;
    gamjeom1: number;
    points2: number;
    gamjeom2: number;
};

// Mirrors \Panel\Fights\Model\Result\KataResult::jsonSerialize(). Kata is a solo performance
// (no second competitor), so it doesn't fit the two-competitor FightResult shape below.
export type KataResult = {
    type: "kata";
    competitorId: number | null;
    judgeScores: number[];
    totalScore: number;
    fightFinishType: string;
    time: string | null;
    discardMinMax: boolean;
    calculationMethod: string;
};

export type KarateKumiteResult = {
    wazari1?: number;
    chui1?: number;
    genten1?: number;
    wazari2?: number;
    chui2?: number;
    genten2?: number;
    decisionScore?: null | "notRecorded" | "unanimous" | "split" | "overtime";
};

//TODO: Not all properties are covered for all result types. This is actually wrestling specific. Each scoreboard should have each its own result type.
//TODO: When Vue js supports discriminated unions, we can change this to be more strict: https://github.com/vuejs/language-tools/issues/4629
export type FightResult = Bjj2Result & WrestlingResult & JudoResult & TaekwondoResult & KarateKumiteResult & FightingResult;

// Mirrors \Panel\PublicBracket\Model\BracketPlayer::jsonSerialize().
// id is null for an awaiting-competitor slot and 0 for a BYE; winner/points are
// null until the fight is decided; cancelled is only emitted when true.
export type BracketPlayer = {
    id: number | null;
    data: {
        publicId: string;
        firstName: string;
        lastName: string;
        academy: string;
        academyId: number;
        photoUrl: string | null;
        nationality?: string;
    };
    winner: boolean | null;
    points: string | null;
    cancelled?: boolean;
};

export type BracketMatch = {
    player1: BracketPlayer;
    player2: BracketPlayer;
    result: FightResult | null;
};

export type PublicFightCompetitor = {
    id: number;
    publicId: string;
    firstName: string;
    lastName: string;
    academy: string;
    branch: string;
    nationality: string;
    status: Status;
    academyId: number;
    photoUrl: string | null;
};

export type UpcomingPublicFight = {
    id: number;
    matId: number;
    firstCompetitor: PublicFightCompetitor;
    secondCompetitor: PublicFightCompetitor;
    roundName: string;
    bracketLevel: number;
    parameterId: number;
    category: string;
    bracketType: BracketType;
    bracketSize: number;
    fightNumber: number | null;
    winnerCompetitor: null;
    scoreboardType: ScoreboardType;
};

export type UpcomingPublicFightWithStartTime = {
    publicFight: UpcomingPublicFight;
    startTime: string;
};

export type UpcomingPublicFightsCollection = {
    result: UpcomingPublicFightWithStartTime[];
    fightQueueStatuses: Record<number, FightQueueStatus>; //MatId => FightQueueStatus
};

export function isQueueBreak(fight: { parameterId: number; bracketType: BracketType }) {
    return fight.parameterId <= 0 && fight.bracketType === BracketTypeEnum.QueueBreak;
}
